import type {
  TimeSeriesComputedSeries,
  TimeSeriesTrendRef,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'

/** A cited value to mark on the chart, snapped to that pen's sample. */
export interface TrendPreviewMarker {
  entryId: string
  timestamp: string
  value: number
  /** When the citation was clicked; a new click re-seeds a deleted marker. */
  requestedAt?: number
}

export type TrendPreviewYRanges = Record<string, { min?: number; max?: number }>

/** The y-range key the agent uses for one axis shared by every pen. */
export const SHARED_AXIS_KEY = 'all'

/** The trend shown beside a chat; only what the embedded chart can render. */
export interface TrendPreview {
  entryIds: string[]
  label: string
  startDatetime?: string
  endDatetime?: string
  computedSeries?: TimeSeriesComputedSeries[]
  contextEntryIds?: string[]
  sessionKey?: string
  replacePens?: boolean
  marker?: TrendPreviewMarker
  hiddenPens?: string[]
  yRanges?: TrendPreviewYRanges
  interval?: number
  intervalUnit?: string
}

export type PreviewTrendHandler = (trend: TrendPreview) => void

export function yRangesFromWire(
  ranges:
    | Record<string, { min?: number | null; max?: number | null }>
    | null
    | undefined
): TrendPreviewYRanges | undefined {
  if (!ranges) return undefined
  const out: TrendPreviewYRanges = {}
  for (const [id, range] of Object.entries(ranges)) {
    out[id] = {
      ...(typeof range.min === 'number' ? { min: range.min } : {}),
      ...(typeof range.max === 'number' ? { max: range.max } : {}),
    }
  }
  return Object.keys(out).length ? out : undefined
}

export function trendPreviewFromRef(trend: TimeSeriesTrendRef): TrendPreview {
  return {
    entryIds: trend.entry_ids,
    label: trend.label,
    startDatetime: trend.start_datetime ?? undefined,
    endDatetime: trend.end_datetime ?? undefined,
    computedSeries: trend.computed_series ?? undefined,
    contextEntryIds: trend.context_entry_ids ?? undefined,
    replacePens: trend.replace_pens ?? undefined,
    interval: trend.interval ?? undefined,
    intervalUnit: trend.interval_unit ?? undefined,
    yRanges: yRangesFromWire(trend.y_ranges),
  }
}

function union(...lists: Array<readonly string[] | undefined>): string[] {
  return [...new Set(lists.flatMap((list) => list ?? []))]
}

/**
 * A cited value already inside the open preview's window is marked without
 * moving the window or the axes: the result carries the open window, so a
 * replacePens merge keeps it as well; outside it, the incoming trend's own
 * window (or the padded fallback) applies as usual.
 */
export function keepOpenWindowForMarker(
  current: TrendPreview | null,
  incoming: TrendPreview
): TrendPreview {
  if (!current?.startDatetime || !current.endDatetime || !incoming.marker) {
    return incoming
  }
  const at = Date.parse(incoming.marker.timestamp)
  const start = Date.parse(current.startDatetime)
  const end = Date.parse(current.endDatetime)
  if (![at, start, end].every(Number.isFinite) || at < start || at > end) {
    return incoming
  }
  const kept: TrendPreview = {
    ...incoming,
    startDatetime: current.startDatetime,
    endDatetime: current.endDatetime,
  }
  delete kept.yRanges
  return kept
}

export function plainPenIds(preview: TrendPreview): string[] {
  return union(
    preview.contextEntryIds ??
      (preview.computedSeries?.length ? [] : preview.entryIds)
  )
}

function shownIds(preview: TrendPreview): Set<string> {
  return new Set(
    union(
      preview.entryIds,
      preview.contextEntryIds,
      (preview.computedSeries ?? []).map((series) => series.entry_id)
    )
  )
}

/**
 * Fold a follow-up trend reference into an open preview: pens and computed
 * series accumulate (same-id computed series replace), hidden pens union, the
 * incoming window, bins and label win, and a replacePens follow-up shows
 * exactly the incoming pens and computed series. Axis ranges merge with the
 * incoming ones winning; on replace, base ranges survive only for pens that
 * stay on the chart. The session key survives so open Process Data tabs track
 * along.
 */
export function mergeTrendPreviews(
  base: TrendPreview,
  incoming: TrendPreview
): TrendPreview {
  const sessionKey = base.sessionKey ?? incoming.sessionKey
  const marker = incoming.marker ?? base.marker
  if (incoming.replacePens) {
    const shown = shownIds(incoming)
    const kept = Object.fromEntries(
      Object.entries(base.yRanges ?? {}).filter(([id]) => shown.has(id))
    )
    const yRanges = { ...kept, ...incoming.yRanges }
    return {
      ...incoming,
      sessionKey,
      marker,
      yRanges: Object.keys(yRanges).length ? yRanges : undefined,
    }
  }
  const yRanges =
    base.yRanges || incoming.yRanges
      ? { ...base.yRanges, ...incoming.yRanges }
      : undefined
  const computedById = new Map(
    [...(base.computedSeries ?? []), ...(incoming.computedSeries ?? [])].map(
      (series) => [series.entry_id, series]
    )
  )
  const computedSeries = [...computedById.values()]
  return {
    entryIds: union(base.entryIds, incoming.entryIds),
    label: incoming.label || base.label,
    startDatetime: incoming.startDatetime ?? base.startDatetime,
    endDatetime: incoming.endDatetime ?? base.endDatetime,
    computedSeries: computedSeries.length ? computedSeries : undefined,
    contextEntryIds: union(plainPenIds(base), plainPenIds(incoming)),
    sessionKey,
    marker,
    hiddenPens: union(base.hiddenPens, incoming.hiddenPens),
    yRanges,
    interval: incoming.interval ?? base.interval,
    intervalUnit: incoming.intervalUnit ?? base.intervalUnit,
  }
}
